/**
 * Adds pitch (up/down) to touch drags. A-Frame's look-controls only yaws on touch — its onTouchMove
 * reads pageX alone — so this writes the pitch and leaves the yaw to look-controls.
 */

// Clamp at straight up / straight down, matching look-controls' mouse-drag limit.
const PITCH_LIMIT_RADIANS = Math.PI / 2;

// A canvas-width drag pitches half a circle: look-controls' yaw scale, divided by width on both axes
// so a diagonal drag pitches as fast as it turns.
const RADIANS_PER_CANVAS_WIDTH = Math.PI;

/** The listeners the drag runs on, kept together so that removing the component can take them all off. */
interface PitchDrag {
    /** Where the finger was when the drag last moved, or null between drags. */
    lastTouchY: number | null;
    onTouchStart: (event: TouchEvent) => void;
    onTouchMove: (event: TouchEvent) => void;
    onTouchEnd: () => void;
}

interface TouchPitch {
    drag?: PitchDrag;
}

AFRAME.registerComponent<TouchPitch>("touchpitch", {
    init: function () {
        const scene = this.el.sceneEl!;

        const drag: PitchDrag = {
            lastTouchY: null,

            // The drag starts on the canvas but follows the finger anywhere, as look-controls does;
            // a touch that starts elsewhere belongs to navigation.
            onTouchStart: (event: TouchEvent) => {
                if (event.touches.length !== 1 || scene.is("vr-mode")) {
                    return;
                }

                drag.lastTouchY = event.touches[0].pageY;
            },

            onTouchMove: (event: TouchEvent) => {
                const lookControls = this.el.components["look-controls"] as any;

                if (drag.lastTouchY === null || !lookControls?.data.enabled
                    || !lookControls.data.touchEnabled) {
                    return;
                }

                const pageY = event.touches[0].pageY;

                // Delta from the last move, not from the drag start: from the start, travel spent
                // past the clamp would have to be unwound before the view moved again.
                const draggedRadians =
                    RADIANS_PER_CANVAS_WIDTH * (pageY - drag.lastTouchY) / scene.canvas.clientWidth;

                drag.lastTouchY = pageY;

                // Matches how look-controls signs the yaw, so both axes follow the finger the same way.
                const direction = lookControls.data.reverseTouchDrag ? 1 : -1;
                const pitch = lookControls.pitchObject.rotation;

                pitch.x = Math.max(-PITCH_LIMIT_RADIANS,
                    Math.min(PITCH_LIMIT_RADIANS, pitch.x - draggedRadians * direction));
            },

            onTouchEnd: () => {
                drag.lastTouchY = null;
            }
        };

        this.drag = drag;

        const listen = () => {
            // A-Frame builds the canvas after the camera's components init.
            if (!scene.canvas) {
                // Cast to DOM typing: A-Frame doesn't know this event and types the third arg as a
                // boolean where addEventListener takes options.
                (scene as unknown as HTMLElement)
                    .addEventListener("render-target-loaded", listen, {once: true});
                return;
            }

            scene.canvas.addEventListener("touchstart", drag.onTouchStart);
            window.addEventListener("touchmove", drag.onTouchMove);
            window.addEventListener("touchend", drag.onTouchEnd);
            window.addEventListener("touchcancel", drag.onTouchEnd);
        };

        listen();
    },

    remove: function () {
        const drag = this.drag;

        if (!drag) {
            return;
        }

        this.el.sceneEl?.canvas?.removeEventListener("touchstart", drag.onTouchStart);
        window.removeEventListener("touchmove", drag.onTouchMove);
        window.removeEventListener("touchend", drag.onTouchEnd);
        window.removeEventListener("touchcancel", drag.onTouchEnd);
    }
});

export {};
